'use client';

import { useIntl } from 'react-intl';
import {
  getFragmentData,
  type FragmentType,
} from '@packages/gql/generated/shopping/fragment-masking';
import { RatingsOverviewFragmentFragmentDoc } from '@packages/gql/generated/shopping/RatingsOverviewFragmentFragmentDoc';
import { ratingsOverviewMessages } from './RatingsOverview.messages';
import { RatingIconsWithReviewCount } from '../RatingIconsWithReviewCount/RatingIconsWithReviewCount';
import { RatingsCount } from '../RatingsCount/RatingsCount';

/* GraphQL */ `
  fragment RatingsOverviewFragment on RatingProduct {
    avgRating
    reviewCount
    reviewStatistics {
      count
      portion
      rating
    }
  }
`;

export type RatingsOverviewProps = {
  data: FragmentType<typeof RatingsOverviewFragmentFragmentDoc>;
};

/**
 * The RatingsOverview component displays the rating icons with the amount of given ratings
 * and a visual statistic of how many ratings are available for a special rating
 */
export const RatingsOverview = ({ data, ...props }: RatingsOverviewProps) => {
  const { avgRating, reviewCount, reviewStatistics } = getFragmentData(
    RatingsOverviewFragmentFragmentDoc,
    data,
  );
  const { formatMessage } = useIntl();

  return (
    <div {...props}>
      <RatingIconsWithReviewCount ratingValue={avgRating} ratingsCount={reviewCount} />
      <div className="mt-2 grid grid-cols-[auto_1fr_auto] gap-0.5">
        {reviewStatistics.map((ratingStatistic, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="col-span-3 grid grid-cols-subgrid items-center gap-1.5" key={index}>
            <div className="text-size-body3">
              {formatMessage(ratingsOverviewMessages.label, { rating: ratingStatistic.rating })}
            </div>
            <div>
              <figure
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={ratingStatistic.portion}
                className="bg-color-grey-light overflow-hidden rounded-[4px]"
              >
                <div
                  className="bg-color-grey-dark h-[12px]"
                  style={{ width: `${ratingStatistic.portion}%` }}
                />
              </figure>
            </div>
            <div>
              <RatingsCount ratingsCount={ratingStatistic.count} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
